import { IComboBox, IComboBoxOption, IPersonaStyles } from '@fluentui/react';
import React, { Fragment, FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import SingleTask from 'components/Tasks/SingleTask';
import DateDividerListItem, {
  DataGroup,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import { globalStackTokensGapMedium } from 'globalStyles';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { addDateDays, getDateTimeDiffMinute, toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { toNameInitial } from 'utils/string';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import { IPList } from "baseComponents/List/IPList";

export class WidgetMyUpcomingTasksConfig {
  period: number;

  constructor() {
    this.period = 7;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.period = newRawConfig.period ?? 7;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetMyUpcomingTasksConfig {
    const newConfig = new WidgetMyUpcomingTasksConfig();
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetMyUpcomingTasksProps extends IWidgetRendererProps {}

const WidgetMyUpcomingTasks: FunctionComponent<IWidgetMyUpcomingTasksProps> = (props: IWidgetMyUpcomingTasksProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = React.useContext(AppContext);
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [tasksDisplay, setTasksDisplay] = React.useState<(Task | DateDivider | DataGroup<Task>)[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTask, setSelectedTask] = React.useState<Task | undefined>(undefined);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  React.useEffect(() => {
    if (tasks && tasks?.length > 0) {
      const newData = insertDividersAndGroupsInList(tasks, getCompareDate, undefined, undefined);
      setTasksDisplay(newData);
    }
  }, [tasks]);

  const loadConfig = (): WidgetMyUpcomingTasksConfig => {
    let newRawConfig: WidgetMyUpcomingTasksConfig = new WidgetMyUpcomingTasksConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();
      const taskCol = props.sharedData?.myTasks ?? [];

      const date = addDateDays(new Date(), config.period);
      const filteredTasks = taskCol
        .filter((t) => {
          const start = t.startDateTime;

          return (
            t.completed === undefined &&
            getDateTimeDiffMinute(start, new Date()) > 0 &&
            getDateTimeDiffMinute(start, date) < 0
          );
        })
        .sort((a, b) => sortOnDate(a.startDateTime, b.startDateTime));

      setTasks(filteredTasks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCompareDate: DateComparer<Task> = (a) => {
    return a.startDateTime;
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  //dismissing the modal is close's job: SingleTask also saves WITHOUT closing (a task type pane that
  //needs the stored task first), and clearing the selection here unmounts it in the middle of that
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 5 } }}>
      {tasks?.length === 0 && (
        <Fragment>
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPStack.Item>
              <IPIconButton iconProps={SkypeCircleCheckIcon} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <IPText>{t('widgets:MyUpcomingTasks.NoItems')}</IPText>
          </IPStack.Item>
        </Fragment>
      )}
      {tasks?.length > 0 && (
        <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPList
              items={tasksDisplay}
              onRenderCell={(task) => {
                if (!task) return null;

                if (task instanceof DateDivider) {
                  return <DateDividerListItem {...task} />;
                }

                if (task instanceof DataGroup) {
                  return null;
                }

                return (
                  <IPPersona
                    size={IPPersonaSize.size40}
                    text={task.name}
                    imageInitials={toNameInitial(appContext.globalDataCache.users.get(task.userId).name)}
                    secondaryText={toLocaleDateTimeMedium(task.startDateTime)}
                    onClick={() => {
                      if (!task) return;

                      setSelectedTask(task);
                    }}
                    styles={personaStyle}
                  />
                );
              }}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetMyUpcomingTasks;

//
// Config
//

interface IWidgetConfigMyUpcomingTasksProps extends IWidgetConfigRendererProps {}

export const WidgetConfigMyUpcomingTasks: FunctionComponent<IWidgetConfigMyUpcomingTasksProps> = (
  props: IWidgetConfigMyUpcomingTasksProps,
) => {
  const { t } = useTranslation(['widgets', 'translation']);

  const loadConfig = (): WidgetMyUpcomingTasksConfig => {
    let newRawConfig: WidgetMyUpcomingTasksConfig = new WidgetMyUpcomingTasksConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const getConfig = (period: number): WidgetMyUpcomingTasksConfig => {
    const config = new WidgetMyUpcomingTasksConfig();
    config.period = period;

    return config;
  };

  const getCurrentOption = (): string => {
    const config = loadConfig();

    return config.period.toString();
  };

  const updateConfig = (period: number) => {
    const config = getConfig(period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const getOptions = (): IComboBoxOption[] => {
    const items: IComboBoxOption[] = [];

    const comboOption1: IComboBoxOption = {
      key: '7',
      text: t('widgets:MyUpcomingTasks.Config.Period.Option1'),
    };
    items.push(comboOption1);

    const comboOption2: IComboBoxOption = {
      key: '14',
      text: t('widgets:MyUpcomingTasks.Config.Period.Option2'),
    };
    items.push(comboOption2);

    const comboOption3: IComboBoxOption = {
      key: '30',
      text: t('widgets:MyUpcomingTasks.Config.Period.Option3'),
    };
    items.push(comboOption3);

    const comboOption4: IComboBoxOption = {
      key: '90',
      text: t('widgets:MyUpcomingTasks.Config.Period.Option4'),
    };
    items.push(comboOption4);

    const comboOption5: IComboBoxOption = {
      key: '180',
      text: t('widgets:MyUpcomingTasks.Config.Period.Option5'),
    };
    items.push(comboOption5);

    return items;
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPComboBox
          defaultSelectedKey={getCurrentOption()}
          options={getOptions()}
          label={t('widgets:MyUpcomingTasks.Config.Period.Label')}
          useComboBoxAsMenuWidth
          onChange={(
            event: React.FormEvent<IComboBox>,
            option?: IComboBoxOption | undefined,
            index?: number | undefined,
            value?: string | undefined,
          ) => {
            if (option) {
              updateConfig(option.key as number);
            }
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
